/* eslint-disable i18next/no-literal-string */
import {RadioButton} from '@dropbox/dig-components/controls';
import {DatePickerInput} from '@dropbox/dig-components/date_picker';
import {FormHelperText, FormLabel, FormRow} from '@dropbox/dig-components/form_row';
import {Select} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {Split, Stack} from '@dropbox/dig-foundations';

import {minutesOfDay, withDate, withMinutesOfDay} from './bannerForm';
import {browserTimeZone, readingDay} from './bannerSchedule';

const HALF_HOURS = Array.from({length: 48}, (_, i) => i * 30);

const timeLabel = (minutes: number) =>
  new Date(2000, 0, 1, Math.floor(minutes / 60), minutes % 60).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

const WallClockInput = ({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const minutes = minutesOfDay(value);
  // Keep a time that isn't on the half hour selectable if one was saved.
  const options = HALF_HOURS.includes(minutes)
    ? HALF_HOURS
    : [...HALF_HOURS, minutes].sort((a, b) => a - b);

  return (
    <FormRow>
      <FormLabel htmlFor={id}>{label}</FormLabel>
      <Split gap="8" alignY="center">
        <Split.Item>
          <DatePickerInput
            id={id}
            value={readingDay(value)}
            onChange={(day) => day && onChange(withDate(value, day))}
          />
        </Split.Item>
        <Split.Item>
          <Select
            id={`${id}-time`}
            aria-label={`${label} time`}
            value={minutes.toString()}
            onChange={(next) => onChange(withMinutesOfDay(value, Number(next)))}
          >
            {options.map((option) => (
              <Select.Option key={option} value={option.toString()}>
                {timeLabel(option)}
              </Select.Option>
            ))}
          </Select>
        </Split.Item>
      </Split>
    </FormRow>
  );
};

export const BannerScheduleField = ({
  startsAt,
  endsAt,
  useLocalTime,
  error,
  onChange,
}: {
  startsAt: string;
  endsAt: string;
  useLocalTime: boolean;
  error?: string;
  onChange: (patch: {starts_at?: string; ends_at?: string; use_local_time?: boolean}) => void;
}) => (
  <Stack gap="12">
    <FormRow>
      <FormLabel id="banner-time-zone-label">Time zone</FormLabel>
      <Stack gap="8" role="radiogroup" aria-labelledby="banner-time-zone-label">
        <Split gap="8" alignY="center">
          <Split.Item>
            <RadioButton
              id="banner-time-pacific"
              name="banner-time-zone"
              checked={!useLocalTime}
              onChange={() => onChange({use_local_time: false})}
            />
          </Split.Item>
          <Split.Item>
            <FormLabel htmlFor="banner-time-pacific">
              Pacific time, for everyone at the same moment
            </FormLabel>
          </Split.Item>
        </Split>
        <Split gap="8" alignY="center">
          <Split.Item>
            <RadioButton
              id="banner-time-local"
              name="banner-time-zone"
              checked={useLocalTime}
              onChange={() => onChange({use_local_time: true})}
            />
          </Split.Item>
          <Split.Item>
            <FormLabel htmlFor="banner-time-local">
              Each viewer&apos;s local time, at the same time of day everywhere
            </FormLabel>
          </Split.Item>
        </Split>
      </Stack>
    </FormRow>

    <Stack gap="12">
      <WallClockInput
        id="banner-starts"
        label="Starts"
        value={startsAt}
        onChange={(starts_at) => onChange({starts_at})}
      />
      <WallClockInput
        id="banner-ends"
        label="Ends"
        value={endsAt}
        onChange={(ends_at) => onChange({ends_at})}
      />
    </Stack>

    {error ? (
      <Text size="small" color="error" role="alert">
        {error}
      </Text>
    ) : (
      <FormHelperText>
        {useLocalTime
          ? `Times are read in each viewer's own time zone. For you that's ${browserTimeZone()}.`
          : 'Times are in Pacific time (America/Los_Angeles), wherever the viewer is.'}
      </FormHelperText>
    )}
  </Stack>
);
